// tabs/NeedsTab.tsx
import { useCallback, useEffect, useState } from 'react';
import { Select, Button, Group, Divider, SimpleGrid, Card, Text, ActionIcon, Flex } from '@mantine/core';
import { IconPlus, IconTrash, IconRefresh } from '@tabler/icons-react';
import Tooltip from '@/components/ui/overlays/Tooltip';
import { useCareNeedService, useACCounsellingServices, LeaderNeedView } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import ConfirmActionModal from './ConfirmActionModal';

interface NeedsTabProps {
  leaderId: string;
  active: boolean;
  onLeaderChange?: () => void;
}

export default function NeedsTab({ leaderId, active, onLeaderChange }: NeedsTabProps) {
  const CareNeeds = useCareNeedService();
  const ACCounsellingServices = useACCounsellingServices();

  const [leaderNeeds, setLeaderNeeds] = useState<LeaderNeedView[]>([]);
  const [allNeeds, setAllNeeds] = useState<any[]>([]);
  const [selectedNeedId, setSelectedNeedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('Confirm Remove');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<(() => Promise<void>) | null>(null);

  const fetchLeaderNeeds = useCallback(async () => {
    if (!leaderId) return;
    setLoading(true);
    try {
      const result = await ACCounsellingServices.leader.getNeeds(leaderId);
      setLeaderNeeds(result);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [leaderId, ACCounsellingServices]);

  useEffect(() => {
    CareNeeds.getAll().then(setAllNeeds).catch(console.error);
  }, [CareNeeds]);

  useEffect(() => { if (active) fetchLeaderNeeds(); }, [active, fetchLeaderNeeds]);

  // leaderNeeds (LeaderNeedView) only carries needID/needName — the actual
  // description lives on the full lookup object from CareNeeds.getAll().
  const getNeedName = (needID: number) =>
    allNeeds.find((n) => n.needID === needID)?.name ?? `Need ${needID}`;

  const getNeedDescription = (needID: number) =>
    allNeeds.find((n) => n.needID === needID)?.description ?? null;

  const handleAddNeed = async () => {
    if (!selectedNeedId || !leaderId) return;
    const existing = leaderNeeds.map((n) => n.needID) ?? [];
    if (existing.includes(Number(selectedNeedId))) {
      toast.error('Duplicate', 'This need is already assigned.');
      return;
    }
    setLoading(true);
    try {
      await ACCounsellingServices.leader.addNeed(leaderId, { needID: Number(selectedNeedId) } as any);
      setSelectedNeedId(null);
      await fetchLeaderNeeds();
      onLeaderChange?.();
      toast.success('Need Added', 'Need assigned to leader.');
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Failed to add need.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveNeed = (leaderNeedRowId: number, needName?: string) => {
    setConfirmTitle('Remove Need');
    setConfirmMessage(`Remove this need${needName ? ` (${needName})` : ''} from the leader?`);
    setConfirmAction(() => async () => {
      setLoading(true);
      try {
        await ACCounsellingServices.leader.removeNeed(leaderId, leaderNeedRowId as any);
        await fetchLeaderNeeds();
        onLeaderChange?.();
        toast.warning('Need Removed', 'Need removed from leader.');
      } catch (e) {
        console.error(e);
        toast.error('Error', 'Failed to remove need.');
      } finally {
        setLoading(false);
      }
    });
    setConfirmOpen(true);
  };

  return (
    <>
      <ConfirmActionModal
        opened={confirmOpen}
        title={confirmTitle}
        message={confirmMessage}
        onConfirm={async () => { if (confirmAction) await confirmAction(); }}
        onClose={() => { setConfirmOpen(false); setConfirmAction(null); }}
      />

      <Group gap="sm" justify="space-between" mb="md">
        <Select
          placeholder="Select a need to add"
          style={{ width: 300 }}
          data={allNeeds
            .filter((n) => !(leaderNeeds.map((ln) => ln.needID) ?? []).includes(n.needID))
            .map((n) => ({ value: String(n.needID), label: n.name ?? `Need ${n.needID}` }))}
          value={selectedNeedId}
          onChange={setSelectedNeedId}
          searchable
        />
        <Group gap="sm">
        <Flex justify="right" align="center">
        <Tooltip label="Refresh">
          <ActionIcon variant="subtle" onClick={fetchLeaderNeeds} loading={loading} size="lg">
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Flex>
        <Button leftSection={<IconPlus size={16} />} onClick={handleAddNeed} disabled={!selectedNeedId} loading={loading}>
          Add Need
        </Button>
        </Group>
      </Group>
      <Divider mb="md" />
      {leaderNeeds.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center">No needs assigned.</Text>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          {leaderNeeds.map((n: any) => {
            const description = getNeedDescription(n.needID);
            return (
              <Card key={n.leaderNeedID ?? n.needID} withBorder radius="md" padding="lg">
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <div>
                    <Text fw={700}>{n.needName ?? getNeedName(n.needID)}</Text>
                    {description && <Text size="sm" c="dimmed">{description}</Text>}
                     <Text size="sm" c="dimmed">{n.caseCount} cases</Text>
                  </div>
                  <ActionIcon
                    variant="light"
                    color="red"
                    onClick={() => handleRemoveNeed(n.leaderNeedID ?? n.needID, n.needName ?? getNeedName(n.needID))}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Card>
            );
          })}
        </SimpleGrid>
      )}
    </>
  );
}